import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { customersAPI } from "../../services/api";
import { gregorianToJalaliStr, getTodayJalali } from "../../utils/PersianDatePicker";
import { ArrowRight, Edit2, Save, X, RefreshCw, Printer, CheckCircle, AlertCircle } from "lucide-react";
import SearchableSelect from "../../components/SearchableSelect";
import { useEscClose } from "../../hooks/useEscClose";
import jalaliMoment from "jalali-moment";

interface Props { customerId: number; onBack: () => void; }

const fmt     = (n: any) => new Intl.NumberFormat("en-US").format(Math.abs(Number(n) || 0));
const fmtSign = (n: any) => { const v = Number(n); return (v > 0 ? "+" : "") + new Intl.NumberFormat("en-US").format(v); };
const currencies = ["USD","AFN","IRR","TRY","PKR","AED","EUR"];
type Tab = "ledger" | "info" | "subs";

const balanceColor = (n: number) => n > 0 ? "#ef4444" : n < 0 ? "#10b981" : "#64748b";
const balanceLabel = (n: number) => n > 0 ? "🔴 قرضدار" : n < 0 ? "🟢 طلبکار" : "✅ تسویه";
const balanceDesc  = (n: number) => n > 0 ? "مشتری به ما قرضدار است" : n < 0 ? "ما به مشتری قرضداریم" : "حساب تسویه است";

// تاریخ و ساعت شمسی
const getNowJalali = () => {
  const now  = new Date();
  const jDate = jalaliMoment(now).locale("fa").format("jYYYY/jMM/jDD");
  const time  = now.toTimeString().slice(0, 5);
  return `${jDate} — ${time}`;
};

// تابع چاپ گزارش
function printReport(c: any, txs: any[], subName?: string) {
  const bal    = Number(c.current_balance);
  const bColor = bal > 0 ? "#dc2626" : bal < 0 ? "#059669" : "#64748b";
  const nowStr = getNowJalali();
  const createdStr = c.created_at ? gregorianToJalaliStr(c.created_at.split(" ")[0]) : "—";

  const rows = txs.map((tx: any, i: number) => {
    const txBal   = Number(tx.balance);
    const txColor = txBal > 0 ? "#dc2626" : txBal < 0 ? "#059669" : "#64748b";
    const isOp    = tx.transaction_type === "opening_balance";
    return `
      <tr style="background:${i%2===0?"#f8faff":"#fff"}">
        <td style="text-align:center;color:#94a3b8;font-size:11px">${i+1}</td>
        <td style="text-align:center;direction:ltr;white-space:nowrap">${tx.date ? gregorianToJalaliStr(tx.date) : "—"}</td>
        <td>${tx.description||"—"}${isOp?` <span style="font-size:9px;background:#ede9fe;color:#7c3aed;padding:1px 5px;border-radius:3px;font-weight:700">اولیه</span>`:""}${tx.reference?`<br/><span style="font-size:10px;color:#94a3b8">مرجع: ${tx.reference}</span>`:""}</td>
        <td style="text-align:center;color:#dc2626;font-weight:700">${Number(tx.debit)>0?fmt(tx.debit):"—"}</td>
        <td style="text-align:center;color:#059669;font-weight:700">${Number(tx.credit)>0?fmt(tx.credit):"—"}</td>
        <td style="text-align:center;font-weight:800;color:${txColor}">${fmtSign(tx.balance)}</td>
        <td style="text-align:center;color:#64748b;font-size:11px">${tx.currency||c.currency}</td>
      </tr>`;
  }).join("");

  const html = `<!DOCTYPE html><html dir="rtl" lang="fa">
  <head><meta charset="UTF-8"/>
  <title>گزارش مشتری — ${c.first_name} ${c.last_name}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;700;900&display=swap');
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:'Vazirmatn',sans-serif;background:#fff;color:#1e293b;padding:28px;font-size:13px}
    .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #8b5cf6;padding-bottom:18px;margin-bottom:20px}
    .company{font-size:20px;font-weight:900;color:#8b5cf6}
    .subtitle{font-size:12px;color:#64748b;margin-top:3px}
    .ccard{background:#f8faff;border:1px solid #e2e8f0;border-radius:10px;padding:14px 18px;margin-bottom:18px;display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
    .il label{font-size:10px;color:#94a3b8;font-weight:700;display:block;margin-bottom:2px}
    .il span{font-size:12px;font-weight:700;color:#1e293b}
    .bbox{background:${bal>0?"#fef2f2":bal<0?"#f0fdf4":"#f8faff"};border:2px solid ${bColor};border-radius:10px;padding:12px 18px;margin-bottom:20px;display:flex;align-items:center;justify-content:space-between}
    table{width:100%;border-collapse:collapse;font-size:12px}
    th{background:#8b5cf6;color:white;padding:9px 10px;font-weight:700;font-size:11px}
    td{padding:8px 10px;border-bottom:1px solid #f1f5f9;vertical-align:middle}
    tfoot td{background:#f8faff;font-weight:900;border-top:2px solid #e2e8f0;font-size:13px}
    .footer{margin-top:24px;text-align:center;color:#94a3b8;font-size:11px;border-top:1px solid #e2e8f0;padding-top:14px;display:flex;justify-content:space-between}
    @media print{body{padding:12px}@page{margin:0.8cm;size:A4}}
  </style></head><body>
  <div class="header">
    <div>
      <div class="company">Hamidi Group Finance</div>
      <div class="subtitle">گزارش کارت حساب مشتری${subName?` — زیرمجموعه: ${subName}`:""}</div>
    </div>
    <div style="text-align:left;color:#64748b;font-size:11px;line-height:1.8">
      <div>🖨️ تاریخ و ساعت چاپ: <strong style="color:#8b5cf6">${nowStr}</strong></div>
      <div>کد مشتری: <strong>${c.code}</strong></div>
      <div>تاریخ ثبت: <strong>${createdStr}</strong></div>
    </div>
  </div>
  <div class="ccard">
    <div class="il"><label>نام مشتری</label><span>${c.first_name} ${c.last_name}</span></div>
    <div class="il"><label>شرکت / تجارت</label><span>${c.company_name||"—"}</span></div>
    <div class="il"><label>شماره تماس</label><span dir="ltr">${c.phone||"—"}</span></div>
    <div class="il"><label>شهر / ولایت</label><span>${c.city||"—"}</span></div>
    <div class="il"><label>کشور</label><span>${c.country||"—"}</span></div>
    <div class="il"><label>ارز</label><span>${c.currency}</span></div>
  </div>
  <div class="bbox">
    <div>
      <div style="font-size:13px;font-weight:700;color:#1e293b;margin-bottom:3px">بیلانس نهایی مشتری</div>
      <div style="font-size:11px;color:${bColor}">${balanceDesc(bal)}</div>
    </div>
    <div style="text-align:left">
      <div style="font-size:26px;font-weight:900;color:${bColor}">${fmtSign(bal)} ${c.currency}</div>
      <div style="font-size:12px;color:${bColor}">${balanceLabel(bal)}</div>
    </div>
  </div>
  <table>
    <thead><tr><th>#</th><th>تاریخ</th><th>شرح معامله</th><th>قرضدار 🔴</th><th>طلبکار 🟢</th><th>مانده</th><th>ارز</th></tr></thead>
    <tbody>${rows||'<tr><td colspan="7" style="text-align:center;padding:20px;color:#94a3b8">هیچ تراکنشی ثبت نشده</td></tr>'}</tbody>
    <tfoot>
      <tr>
        <td colspan="3" style="text-align:right;font-size:12px">جمع کل</td>
        <td style="text-align:center;color:#dc2626">${fmt(txs.reduce((s:number,t:any)=>s+Number(t.debit),0))}</td>
        <td style="text-align:center;color:#059669">${fmt(txs.reduce((s:number,t:any)=>s+Number(t.credit),0))}</td>
        <td style="text-align:center;color:${bColor}">${fmtSign(bal)}</td>
        <td style="text-align:center">${c.currency}</td>
      </tr>
    </tfoot>
  </table>
  <div class="footer">
    <span>این گزارش توسط سیستم مدیریت مالی Hamidi Group تولید شده است</span>
    <span>🖨️ ${nowStr}</span>
  </div>
  <script>window.onload=()=>window.print();</script>
  </body></html>`;

  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
}

export default function CustomerProfile({ customerId, onBack }: Props) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [tab, setTab]           = useState<Tab>("ledger");
  const [data, setData]         = useState<any>(null);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [syncing, setSyncing]   = useState(false);
  const [toast, setToast]       = useState<{ msg: string; ok: boolean } | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  useEscClose(() => { if (editing) setEditing(false); else onBack(); });
  const [subData, setSubData]   = useState<Record<number,any>>({});
  const [loadingSub, setLoadingSub] = useState<number|null>(null);

  const showToast = (msg: string, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3500); };

  const load = async () => {
    setLoading(true);
    try {
      const r = await customersAPI.get(customerId);
      if (r.success) { setData(r); setEditForm(r.customer); }
    } catch { showToast("خطا در اتصال", false); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [customerId]);

  const handleSync = async () => {
    setSyncing(true);
    const r = await customersAPI.syncJournal(customerId);
    if (r.success) { showToast("همگام‌سازی انجام شد"); load(); }
    else showToast(r.message, false);
    setSyncing(false);
  };

  // بارگذاری تراکنش‌های زیرمجموعه برای چاپ
  const loadSubAndPrint = async (sub: any) => {
    setLoadingSub(sub.id);
    try {
      const r = await customersAPI.get(sub.id);
      if (r.success) {
        setSubData(d => ({...d, [sub.id]: r}));
        printReport(r.customer, r.transactions || [], `${data?.customer?.first_name} ${data?.customer?.last_name}`);
      } else showToast("خطا در بارگذاری", false);
    } catch { showToast("خطا در اتصال", false); }
    setLoadingSub(null);
  };

  const card: React.CSSProperties = { borderRadius:14, padding:18, background:dark?"rgba(255,255,255,0.03)":"#fff", border:`1px solid ${T.cardBorder}`, marginBottom:14 };
  const inp: React.CSSProperties  = { width:"100%", padding:"9px 12px", background:dark?"rgba(255,255,255,0.05)":"#f8faff", border:`1.5px solid ${dark?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)"}`, borderRadius:8, color:T.text, fontSize:13, fontFamily:"'Vazirmatn','Segoe UI',sans-serif", direction:"rtl" as const, outline:"none", boxSizing:"border-box" as const };
  const lbl: React.CSSProperties  = { fontSize:11, fontWeight:700, color:T.subtext, display:"block", marginBottom:5 };
  const btn = (bg="linear-gradient(135deg,#8b5cf6,#6d28d9)"): React.CSSProperties => ({ display:"flex", alignItems:"center", gap:7, padding:"9px 18px", borderRadius:10, border:"none", background:bg, color:"white", cursor:"pointer", fontSize:13, fontFamily:"inherit", fontWeight:700 });

  if (loading) return (
    <div style={{ textAlign:"center", padding:80, color:T.subtext, fontFamily:"'Vazirmatn','Segoe UI',sans-serif" }}>
      <div style={{ width:40, height:40, border:"3px solid rgba(139,92,246,0.2)", borderTop:"3px solid #8b5cf6", borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 16px" }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const c   = data?.customer;
  const txs = data?.transactions || [];
  const sub = data?.subs || [];
  const bal = Number(c?.current_balance);
  const bColor = balanceColor(bal);

  const editBalVal = editForm.opening_balance_type === "credit"
    ? -Math.abs(parseFloat(editForm.opening_balance || "0"))
    : Math.abs(parseFloat(editForm.opening_balance || "0"));

  return (
    <div style={{ padding:"24px 28px 48px", fontFamily:"'Vazirmatn','Segoe UI',sans-serif" }} dir="rtl">

      {toast && (
        <div style={{ position:"fixed", top:20, left:"50%", transform:"translateX(-50%)", zIndex:9999, padding:"12px 24px", borderRadius:12, background:dark?"#1e293b":"#fff", border:`2px solid ${toast.ok?"#10b981":"#ef4444"}`, boxShadow:"0 8px 32px rgba(0,0,0,0.25)", color:toast.ok?"#10b981":"#ef4444", fontSize:13, fontWeight:700, fontFamily:"inherit", display:"flex", alignItems:"center", gap:8 }}>
          {toast.ok?<CheckCircle size={15}/>:<AlertCircle size={15}/>} {toast.msg}
        </div>
      )}

      {/* هدر */}
      <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:24, flexWrap:"wrap" }}>
        <button onClick={onBack} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:10, border:`1px solid ${T.cardBorder}`, background:"transparent", color:T.subtext, cursor:"pointer", fontSize:13, fontFamily:"inherit" }}>
          <ArrowRight size={15}/> بازگشت
        </button>
        <div style={{ width:52, height:52, borderRadius:14, background:"linear-gradient(135deg,#8b5cf6,#6d28d9)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, color:"white", fontWeight:900, flexShrink:0 }}>
          {c?.first_name?.[0]}{c?.last_name?.[0]||""}
        </div>
        <div>
          <h2 style={{ margin:0, fontSize:20, fontWeight:900, color:T.text }}>{c?.first_name} {c?.last_name}</h2>
          <div style={{ fontSize:12, color:T.subtext, marginTop:2 }}>
            <span style={{ color:"#8b5cf6", fontWeight:700 }}>{c?.code}</span>
            {c?.company_name && ` • ${c.company_name}`}
            {c?.parent_first_name && ` • زیرمجموعه: ${c.parent_first_name} ${c.parent_last_name}`}
            {c?.created_at && <span style={{ marginRight:8, color:T.subtext }}>📅 {gregorianToJalaliStr(c.created_at.split(" ")[0])}</span>}
          </div>
        </div>
        <div style={{ marginRight:"auto", padding:"10px 20px", borderRadius:12, background:`${bColor}12`, border:`1px solid ${bColor}40`, textAlign:"center" }}>
          <div style={{ fontSize:20, fontWeight:900, color:bColor }}>{fmtSign(bal)} {c?.currency}</div>
          <div style={{ fontSize:11, color:bColor, opacity:0.8 }}>{balanceLabel(bal)}</div>
          <div style={{ fontSize:10, color:T.subtext, marginTop:1 }}>{balanceDesc(bal)}</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={handleSync} disabled={syncing} style={{...btn("linear-gradient(135deg,#3b82f6,#2563eb)"),padding:"8px 14px"}}>
            <RefreshCw size={13} style={{ animation:syncing?"spin 1s linear infinite":"none" }}/> {syncing?"...":"همگام‌سازی"}
          </button>
          <button onClick={()=>printReport(c, txs)} style={{...btn("linear-gradient(135deg,#10b981,#059669)"),padding:"8px 14px"}}>
            <Printer size={13}/> چاپ
          </button>
        </div>
      </div>

      {/* تب‌ها */}
      <div style={{ display:"flex", gap:4, marginBottom:24, borderBottom:`1px solid ${T.cardBorder}` }}>
        {([["ledger","📒 کارت حساب"],["info","👤 اطلاعات"],["subs",`👥 زیرمجموعه‌ها (${sub.length})`]] as [Tab,string][]).map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{ padding:"10px 20px", border:"none", background:"transparent", cursor:"pointer", fontSize:13, fontWeight:700, fontFamily:"inherit", color:tab===id?"#8b5cf6":T.subtext, borderBottom:tab===id?"2px solid #8b5cf6":"2px solid transparent", marginBottom:-1, transition:"all 0.2s" }}>{label}</button>
        ))}
      </div>

      {/* ══ کارت حساب ══ */}
      {tab==="ledger" && (
        <div>
          {txs.length===0 ? (
            <div style={{...card,textAlign:"center",padding:60,color:T.subtext}}>
              <div style={{ fontSize:40, marginBottom:10 }}>📒</div>
              <div style={{ marginBottom:12 }}>هیچ تراکنشی ثبت نشده</div>
              <button onClick={handleSync} style={btn()}><RefreshCw size={13}/> همگام‌سازی با جورنال</button>
            </div>
          ) : (
            <div style={{ borderRadius:14, overflow:"hidden", border:`1px solid ${T.cardBorder}` }}>
              <div style={{ display:"grid", gridTemplateColumns:"40px 110px 1fr 140px 140px 140px 70px", gap:8, padding:"12px 16px", background:dark?"rgba(139,92,246,0.12)":"rgba(139,92,246,0.08)", borderBottom:`1px solid ${T.cardBorder}`, fontSize:11, fontWeight:700, color:"#8b5cf6" }}>
                <span>#</span><span>تاریخ</span><span>شرح معامله</span>
                <span style={{ textAlign:"center" }}>قرضدار 🔴</span>
                <span style={{ textAlign:"center" }}>طلبکار 🟢</span>
                <span style={{ textAlign:"center" }}>مانده</span>
                <span style={{ textAlign:"center" }}>ارز</span>
              </div>
              {txs.map((tx:any,i:number)=>{
                const txBal = Number(tx.balance);
                const txBC  = balanceColor(txBal);
                const isOp  = tx.transaction_type==="opening_balance";
                return (
                  <div key={tx.id} style={{ display:"grid", gridTemplateColumns:"40px 110px 1fr 140px 140px 140px 70px", gap:8, padding:"11px 16px", borderBottom:`1px solid ${T.cardBorder}`, alignItems:"center", background:isOp?(dark?"rgba(139,92,246,0.06)":"rgba(139,92,246,0.04)"):i%2===0?"transparent":(dark?"rgba(255,255,255,0.01)":"rgba(0,0,0,0.01)") }}>
                    <span style={{ fontSize:10, color:T.subtext, textAlign:"center" }}>{i+1}</span>
                    <span style={{ fontSize:12, color:T.text, direction:"ltr" }}>{tx.date?gregorianToJalaliStr(tx.date):"—"}</span>
                    <div>
                      <div style={{ fontSize:12, fontWeight:isOp?700:500, color:T.text }}>{tx.description||"—"}</div>
                      {tx.reference && <div style={{ fontSize:10, color:T.subtext }}>مرجع: {tx.reference}</div>}
                      {isOp && <span style={{ fontSize:9, background:"rgba(139,92,246,0.15)", color:"#8b5cf6", padding:"1px 6px", borderRadius:4, fontWeight:700 }}>بیلانس اولیه</span>}
                    </div>
                    <div style={{ textAlign:"center" }}>
                      {Number(tx.debit)>0?<span style={{ fontSize:13, fontWeight:700, color:"#ef4444" }}>{fmt(tx.debit)}</span>:<span style={{ color:T.subtext, opacity:0.3 }}>—</span>}
                    </div>
                    <div style={{ textAlign:"center" }}>
                      {Number(tx.credit)>0?<span style={{ fontSize:13, fontWeight:700, color:"#10b981" }}>{fmt(tx.credit)}</span>:<span style={{ color:T.subtext, opacity:0.3 }}>—</span>}
                    </div>
                    <div style={{ textAlign:"center", fontSize:13, fontWeight:800, color:txBC }}>{fmtSign(tx.balance)}</div>
                    <div style={{ textAlign:"center", fontSize:11, color:T.subtext }}>{tx.currency||c?.currency}</div>
                  </div>
                );
              })}
              {/* جمع کل */}
              <div style={{ display:"grid", gridTemplateColumns:"40px 110px 1fr 140px 140px 140px 70px", gap:8, padding:"13px 16px", background:dark?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.04)", borderTop:`2px solid ${T.cardBorder}`, fontWeight:800 }}>
                <span/><span/>
                <span style={{ fontSize:13, color:T.text }}>جمع کل</span>
                <span style={{ textAlign:"center", fontSize:13, color:"#ef4444" }}>{fmt(txs.reduce((s:number,t:any)=>s+Number(t.debit),0))}</span>
                <span style={{ textAlign:"center", fontSize:13, color:"#10b981" }}>{fmt(txs.reduce((s:number,t:any)=>s+Number(t.credit),0))}</span>
                <span style={{ textAlign:"center", fontSize:14, color:bColor }}>{fmtSign(bal)}</span>
                <span style={{ textAlign:"center", fontSize:11, color:T.subtext }}>{c?.currency}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ اطلاعات ══ */}
      {tab==="info" && (
        <div>
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:14 }}>
            {!editing
              ? <button onClick={()=>setEditing(true)} style={{...btn("linear-gradient(135deg,#64748b,#475569)"),padding:"8px 16px"}}><Edit2 size={13}/> ویرایش</button>
              : <div style={{ display:"flex", gap:8 }}>
                  <button onClick={()=>{setEditing(false);setEditForm(c);}} style={{ display:"flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,border:`1px solid ${T.cardBorder}`,background:"transparent",color:T.text,cursor:"pointer",fontSize:13,fontFamily:"inherit" }}><X size={13}/> انصراف</button>
                  <button onClick={async()=>{
                    setSaving(true);
                    const raw = parseFloat(editForm.opening_balance_raw ?? String(editBalVal));
                    const r = await customersAPI.update({
                      ...editForm,
                      opening_balance: Math.abs(raw),
                      opening_balance_type: raw>=0?"debit":"credit",
                    });
                    if(r.success){showToast("ذخیره شد");setEditing(false);load();}
                    else showToast(r.message,false);
                    setSaving(false);
                  }} disabled={saving} style={btn()}><Save size={13}/> {saving?"...":"ذخیره"}</button>
                </div>
            }
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 }}>
            {[
              {label:"نام",key:"first_name"},{label:"تخلص",key:"last_name"},
              {label:"نام شرکت",key:"company_name"},{label:"شماره تماس",key:"phone"},
              {label:"شماره تماس ۲",key:"phone2"},{label:"شهر",key:"city"},
              {label:"کشور",key:"country"},
            ].map(({label,key})=>(
              <div key={key} style={{...card,marginBottom:0}}>
                <div style={{ fontSize:10, color:T.subtext, marginBottom:4 }}>{label}</div>
                {editing
                  ? <input value={editForm[key]||""} onChange={e=>setEditForm((f:any)=>({...f,[key]:e.target.value}))} style={inp}/>
                  : <div style={{ fontSize:14, fontWeight:600, color:T.text }}>{c?.[key]||"—"}</div>
                }
              </div>
            ))}
            <div style={{...card,marginBottom:0}}>
              <div style={{ fontSize:10, color:T.subtext, marginBottom:4 }}>ارز</div>
              {editing
                ? <SearchableSelect options={currencies.map(c=>({value:c,label:c}))} value={editForm.currency||"USD"} onChange={v=>setEditForm((f:any)=>({...f,currency:v||"USD"}))} placeholder="— ارز"/>
                : <div style={{ fontSize:14, fontWeight:600, color:T.text }}>{c?.currency}</div>
              }
            </div>
            <div style={{...card,marginBottom:0}}>
              <div style={{ fontSize:10, color:T.subtext, marginBottom:4 }}>بیلانس اولیه</div>
              {editing ? (
                <>
                  <input type="number"
                    value={editForm.opening_balance_raw ?? String(editBalVal)}
                    onChange={e=>{
                      const raw = parseFloat(e.target.value||"0");
                      setEditForm((f:any)=>({...f, opening_balance_raw:e.target.value, opening_balance:Math.abs(raw), opening_balance_type:raw>=0?"debit":"credit"}));
                    }}
                    placeholder="مثبت=قرضدار / منفی=طلبکار"
                    style={{...inp,color:editBalVal>0?"#ef4444":editBalVal<0?"#10b981":T.text,fontWeight:700}}
                  />
                  <div style={{ fontSize:10, marginTop:4, color:parseFloat(editForm.opening_balance_raw||"0")>=0?"#ef4444":"#10b981", fontWeight:700 }}>
                    {parseFloat(editForm.opening_balance_raw||"0")>0?"🔴 قرضدار":parseFloat(editForm.opening_balance_raw||"0")<0?"🟢 طلبکار":"✅ تسویه"}
                  </div>
                </>
              ) : (
                <div style={{ fontSize:14, fontWeight:700, color:c?.opening_balance_type==="debit"?"#ef4444":"#10b981" }}>
                  {c?.opening_balance_type==="debit"?"+":"-"}{fmt(c?.opening_balance)} {c?.currency}
                  <span style={{ fontSize:10, marginRight:6, opacity:0.7 }}>({c?.opening_balance_type==="debit"?"قرضدار":"طلبکار"})</span>
                </div>
              )}
            </div>
            <div style={{...card,marginBottom:0}}>
              <div style={{ fontSize:10, color:T.subtext, marginBottom:4 }}>تاریخ ثبت</div>
              <div style={{ fontSize:14, fontWeight:600, color:T.text }}>{c?.created_at?gregorianToJalaliStr(c.created_at.split(" ")[0]):"—"}</div>
            </div>
            <div style={{...card,marginBottom:0}}>
              <div style={{ fontSize:10, color:T.subtext, marginBottom:4 }}>وضعیت</div>
              {editing
                ? <SearchableSelect options={[{value:"active",label:"فعال"},{value:"inactive",label:"غیرفعال"}]} value={editForm.status||"active"} onChange={v=>setEditForm((f:any)=>({...f,status:v||"active"}))} placeholder="— انتخاب"/>
                : <span style={{ fontSize:13, fontWeight:700, color:c?.status==="active"?"#10b981":"#f59e0b" }}>{c?.status==="active"?"فعال":"غیرفعال"}</span>
              }
            </div>
            <div style={{...card,marginBottom:0,gridColumn:"1/-1"}}>
              <div style={{ fontSize:10, color:T.subtext, marginBottom:4 }}>آدرس</div>
              {editing
                ? <input value={editForm.address||""} onChange={e=>setEditForm((f:any)=>({...f,address:e.target.value}))} style={inp}/>
                : <div style={{ fontSize:14, color:T.text }}>{c?.address||"—"}</div>
              }
            </div>
            {(editing||c?.notes) && (
              <div style={{...card,marginBottom:0,gridColumn:"1/-1"}}>
                <label style={lbl}>یادداشت</label>
                {editing
                  ? <textarea value={editForm.notes||""} onChange={e=>setEditForm((f:any)=>({...f,notes:e.target.value}))} rows={3} style={{...inp,resize:"vertical" as const}}/>
                  : <div style={{ fontSize:13, color:T.text }}>{c?.notes}</div>
                }
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ زیرمجموعه‌ها ══ */}
      {tab==="subs" && (
        <div>
          {sub.length===0 ? (
            <div style={{...card,textAlign:"center",padding:50,color:T.subtext}}>
              <div style={{ fontSize:36, marginBottom:10 }}>👥</div>
              <div>این مشتری زیرمجموعه‌ای ندارد</div>
              <div style={{ fontSize:12, marginTop:6 }}>برای افزودن، مشتری جدید ثبت کنید و این مشتری را به عنوان والد انتخاب کنید</div>
            </div>
          ) : (
            <>
              <div style={{...card,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{sub.length} زیرمجموعه</div>
                  <div style={{ fontSize:11, color:T.subtext }}>بیلانس این مشتری = مجموع بیلانس زیرمجموعه‌ها</div>
                </div>
                <div style={{ fontSize:18, fontWeight:900, color:bColor }}>{fmtSign(bal)} {c?.currency}</div>
              </div>
              {sub.map((s:any)=>{
                const sBal   = Number(s.current_balance);
                const sBColor = balanceColor(sBal);
                return (
                  <div key={s.id} style={{...card, borderRight:`4px solid ${sBColor}`}}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                        <div style={{ width:38, height:38, borderRadius:10, background:"linear-gradient(135deg,#64748b,#475569)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, color:"white", fontWeight:700 }}>
                          {s.first_name?.[0]}{s.last_name?.[0]||""}
                        </div>
                        <div>
                          <div style={{ fontSize:14, fontWeight:700, color:T.text }}>{s.first_name} {s.last_name}</div>
                          <div style={{ fontSize:11, color:T.subtext }}>{s.code} • {s.tx_count} معامله {s.created_at && `• ${gregorianToJalaliStr(s.created_at.split(" ")[0])}`}</div>
                        </div>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ textAlign:"left" }}>
                          <div style={{ fontSize:16, fontWeight:800, color:sBColor }}>{fmtSign(sBal)} {s.currency}</div>
                          <div style={{ fontSize:10, color:sBColor }}>{balanceLabel(sBal)}</div>
                        </div>
                        {/* دکمه چاپ گزارش زیرمجموعه */}
                        <button
                          onClick={()=>loadSubAndPrint(s)}
                          disabled={loadingSub===s.id}
                          style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 12px", borderRadius:8, border:"none", background:"linear-gradient(135deg,#10b981,#059669)", color:"white", cursor:"pointer", fontSize:12, fontFamily:"inherit", fontWeight:700 }}
                        >
                          <Printer size={12}/> {loadingSub===s.id?"...":"گزارش"}
                        </button>
                      </div>
                    </div>
                    {bal!==0 && (
                      <div style={{ marginTop:10, height:4, borderRadius:2, background:dark?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)", overflow:"hidden" }}>
                        <div style={{ height:"100%", borderRadius:2, background:sBColor, width:`${Math.min(Math.abs(sBal/bal)*100,100)}%` }}/>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}